"use client";

import { useState } from "react";
import { MapPin, Users, BookOpen, X } from "lucide-react";
import type { TimelineEvent as TimelineEventType } from "@/lib/types";

interface TimelineEventProps {
  event: TimelineEventType;
  position: "above" | "below";
  eraColor: string;
}

export default function TimelineEvent({
  event,
  position,
  eraColor,
}: TimelineEventProps) {
  const [expanded, setExpanded] = useState(false);

  const yearLabel =
    event.approxYear < 0
      ? `${Math.abs(event.approxYear)} BC`
      : `${event.approxYear} AD`;

  return (
    <>
      <div
        className={`flex flex-col items-center ${
          position === "above" ? "flex-col-reverse" : ""
        }`}
        style={{ minWidth: 180, maxWidth: 220 }}
      >
        {/* Connector line */}
        <div
          className="w-px"
          style={{
            height: 32,
            background: `linear-gradient(${
              position === "above" ? "to top" : "to bottom"
            }, ${eraColor}, transparent)`,
          }}
        />

        {/* Dot on the axis */}
        <div
          className="w-3 h-3 rounded-full border-2 shrink-0"
          style={{
            borderColor: eraColor,
            background: "#0a0e1a",
          }}
        />

        {/* Card */}
        <button
          onClick={() => setExpanded(true)}
          className="text-left rounded-lg p-3 transition-all hover:scale-[1.03] cursor-pointer"
          style={{
            background: "rgba(10, 14, 26, 0.85)",
            border: `1px solid ${eraColor}33`,
            maxWidth: 200,
          }}
        >
          <p className="text-[10px] font-medium mb-0.5" style={{ color: eraColor }}>
            {yearLabel}
          </p>
          <h4
            className="text-sm font-semibold leading-tight mb-1"
            style={{ color: "#e8e0d0" }}
          >
            {event.title}
          </h4>
          <p
            className="text-xs leading-relaxed line-clamp-2"
            style={{ color: "rgba(232, 224, 208, 0.5)" }}
          >
            {event.description}
          </p>

          {/* People chips */}
          {event.people && event.people.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-2">
              {event.people.slice(0, 3).map((p) => (
                <span
                  key={p}
                  className="px-1.5 py-0.5 rounded text-[10px]"
                  style={{
                    backgroundColor: "rgba(200, 164, 94, 0.12)",
                    color: "#c8a45e",
                  }}
                >
                  {p}
                </span>
              ))}
            </div>
          )}
        </button>
      </div>

      {/* Expanded Modal */}
      {expanded && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: "rgba(0, 0, 0, 0.7)" }}
          onClick={() => setExpanded(false)}
        >
          <div
            className="rounded-xl p-6 max-w-md w-full shadow-2xl"
            style={{
              background: "rgba(10, 14, 26, 0.98)",
              border: `1px solid ${eraColor}44`,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between mb-3">
              <div>
                <p className="text-xs font-medium mb-1" style={{ color: eraColor }}>
                  {yearLabel} - {event.era}
                </p>
                <h3 className="text-lg font-semibold" style={{ color: "#e8e0d0" }}>
                  {event.title}
                </h3>
              </div>
              <button
                onClick={() => setExpanded(false)}
                className="p-1 rounded hover:bg-white/10 transition-colors"
                style={{ color: "#e8e0d0" }}
              >
                <X size={18} />
              </button>
            </div>

            <p
              className="text-sm leading-relaxed mb-4"
              style={{ color: "rgba(232, 224, 208, 0.7)" }}
            >
              {event.description}
            </p>

            {event.location && (
              <div className="flex items-center gap-2 mb-3">
                <MapPin size={14} style={{ color: "#4a7dbd" }} />
                <span className="text-sm" style={{ color: "#4a7dbd" }}>
                  {event.location}
                </span>
              </div>
            )}

            {event.people && event.people.length > 0 && (
              <div className="mb-3">
                <div className="flex items-center gap-1.5 mb-1.5">
                  <Users size={14} style={{ color: "rgba(232, 224, 208, 0.4)" }} />
                  <span
                    className="text-xs uppercase tracking-wider font-semibold"
                    style={{ color: "rgba(232, 224, 208, 0.4)" }}
                  >
                    People
                  </span>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {event.people.map((p) => (
                    <span
                      key={p}
                      className="px-2 py-0.5 rounded text-xs"
                      style={{
                        backgroundColor: "rgba(200, 164, 94, 0.12)",
                        color: "#c8a45e",
                        border: "1px solid rgba(200, 164, 94, 0.2)",
                      }}
                    >
                      {p}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {event.verses && event.verses.length > 0 && (
              <div>
                <div className="flex items-center gap-1.5 mb-1.5">
                  <BookOpen size={14} style={{ color: "rgba(232, 224, 208, 0.4)" }} />
                  <span
                    className="text-xs uppercase tracking-wider font-semibold"
                    style={{ color: "rgba(232, 224, 208, 0.4)" }}
                  >
                    Verses
                  </span>
                </div>
                <div className="space-y-1.5">
                  {event.verses.map((v, i) => (
                    <div
                      key={i}
                      className="p-2 rounded text-sm"
                      style={{
                        backgroundColor: "rgba(232, 224, 208, 0.04)",
                        border: "1px solid rgba(232, 224, 208, 0.06)",
                      }}
                    >
                      <span className="text-xs font-medium" style={{ color: "#c8a45e" }}>
                        {v.book} {v.chapter}:{v.verse}
                      </span>
                      {v.text && (
                        <p
                          className="text-xs mt-0.5"
                          style={{ color: "rgba(232, 224, 208, 0.6)" }}
                        >
                          {v.text}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
